import { Controller } from '@hotwired/stimulus'
import { 
  calculateCheckoutCosts, 
  formatCurrency
} from '../services/cost-calculator'

/**
 * Shipping Costs Controller
 * 
 * Manages shipping option selection and cost display updates on the checkout page.
 * Follows Single Responsibility Principle - only concerned with shipping-related UI updates.
 * 
 * Uses class-based selectors to work with the existing checkout form DOM structure:
 *   - .shippingcost: Element displaying the shipping cost
 *   - .totalcost: Element displaying the total cost
 *   - .rates: Container for shipping rate price displays (hidden for collect)
 *   - .no-rates: Container shown when no rates needed (collect account)
 *   - .collect-account-field: Collect account number input container
 *   - #collect_account: The collect account number input
 *   - #freight-forwarding: Freight forwarding info section
 * 
 * Values:
 *   - collectLabel: Translated text for "(Collect)" label
 * 
 * Actions:
 *   - updateCosts: Recalculates and displays all costs
 * 
 * Usage in ERB:
 *   Add to the checkout form wrapper:
 *   <div data-controller="shipping-costs" 
 *        data-action="change@document->shipping-costs#updateCosts"
 *        data-shipping-costs-collect-label-value="<%= t('.shipping_collect_label') %>">
 *   </div>
 */
export default class extends Controller {
  static values = {
    collectLabel: { type: String, default: 'Collect' }
  }

  static STORAGE_KEY = 'checkout_selected_shipping'
  static TERMS_STORAGE_KEY = 'checkout_shipping_terms'
  static ACCOUNT_STORAGE_KEY = 'checkout_collect_account'

  connect() {
    this.updateCosts()
    
    // Listen for address updates from checkout controller
    this.boundHandleAddressUpdate = this.updateCosts.bind(this)
    document.addEventListener('checkout:address-updated', this.boundHandleAddressUpdate)
    
    // Listen for Turbo Frame loads to restore shipping selection
    this.boundRestoreSelection = this.restoreShippingSelection.bind(this)
    document.addEventListener('turbo:frame-load', this.boundRestoreSelection)
    
    // Save selections when changed
    this.boundSaveSelection = this.saveSelection.bind(this)
    this.element.addEventListener('change', this.boundSaveSelection)
    
    // Save account number on input (not just change)
    this.boundSaveAccountNumber = this.saveAccountNumber.bind(this)
    this.element.addEventListener('input', this.boundSaveAccountNumber)
    
    // Try to restore selections on initial connect (for non-Turbo Frame loads)
    this.restoreShippingSelection()
    this.restoreShippingTerms()
    this.restoreCollectAccount()
  }

  disconnect() {
    document.removeEventListener('checkout:address-updated', this.boundHandleAddressUpdate)
    document.removeEventListener('turbo:frame-load', this.boundRestoreSelection)
    this.element.removeEventListener('change', this.boundSaveSelection)
    this.element.removeEventListener('input', this.boundSaveAccountNumber)
  }

  /**
   * Saves the selection based on which field changed
   * @param {Event} event - The change event
   */
  saveSelection(event) {
    const target = event.target
    
    if (target.name === 'shipping') {
      this.saveToStorage(this.constructor.STORAGE_KEY, target.value)
    } else if (target.name === 'shipping_terms') {
      this.saveToStorage(this.constructor.TERMS_STORAGE_KEY, target.value)
    } else if (target.id === 'collect_account') {
      this.saveToStorage(this.constructor.ACCOUNT_STORAGE_KEY, target.value)
    }
  }

  /**
   * Saves collect account number on input (for real-time save while typing)
   * @param {Event} event - The input event
   */
  saveAccountNumber(event) {
    if (event.target.id === 'collect_account') {
      this.saveToStorage(this.constructor.ACCOUNT_STORAGE_KEY, event.target.value)
    }
  }

  /**
   * Helper to save value to sessionStorage with error handling
   * @param {string} key - Storage key
   * @param {string} value - Value to save
   */
  saveToStorage(key, value) {
    try {
      sessionStorage.setItem(key, value)
    } catch (e) {
      console.warn(`Could not save ${key}:`, e)
    }
  }

  /**
   * Helper to get value from sessionStorage with error handling
   * @param {string} key - Storage key
   * @returns {string|null} The stored value or null
   */
  getFromStorage(key) {
    try {
      return sessionStorage.getItem(key)
    } catch (e) {
      console.warn(`Could not retrieve ${key}:`, e)
      return null
    }
  }

  /**
   * Attempts to restore the previously selected shipping option
   * Falls back to default selection if saved option is no longer available
   * @param {Event} [event] - Optional turbo:frame-load event
   */
  restoreShippingSelection(event) {
    // If triggered by turbo:frame-load, only act on shipping-rates-frame
    if (event && event.target && event.target.id !== 'shipping-rates-frame') {
      return
    }
    
    const savedSelection = this.getFromStorage(this.constructor.STORAGE_KEY)
    if (!savedSelection) return
    
    const shippingRadio = document.querySelector(`input[name="shipping"][value="${savedSelection}"]`)
    if (shippingRadio && !shippingRadio.checked) {
      shippingRadio.checked = true
      // Trigger change event to update costs
      shippingRadio.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  /**
   * Restores the shipping terms selection (Prepaid/Freight Collect/Third Party)
   */
  restoreShippingTerms() {
    const savedTerms = this.getFromStorage(this.constructor.TERMS_STORAGE_KEY)
    if (!savedTerms) return
    
    const termsRadio = document.querySelector(`input[name="shipping_terms"][value="${savedTerms}"]`)
    if (termsRadio && !termsRadio.checked) {
      termsRadio.checked = true
      // Trigger change event to update UI visibility
      termsRadio.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  /**
   * Restores the collect account number
   */
  restoreCollectAccount() {
    const savedAccount = this.getFromStorage(this.constructor.ACCOUNT_STORAGE_KEY)
    if (!savedAccount) return
    
    const accountInput = document.getElementById('collect_account')
    if (accountInput && accountInput.value !== savedAccount) {
      accountInput.value = savedAccount
    }
  }

  /**
   * Handles any shipping-related change event
   */
  updateCosts() {
    const costs = calculateCheckoutCosts()
    
    this.updateCostDisplay(costs)
    this.updateShippingTermsVisibility(costs)
    this.updateFreightForwarderVisibility(costs)
  }

  /**
   * Updates the cost display elements using class selectors
   * @param {Object} costs - Calculated costs object
   */
  updateCostDisplay(costs) {
    const shippingCostEl = document.querySelector('.shippingcost')
    const totalCostEl = document.querySelector('.totalcost')
    
    if (shippingCostEl) {
      // Update the text but preserve hidden field if it exists
      const hiddenField = shippingCostEl.querySelector('input[type="hidden"]')
      
      // Build new content
      if (hiddenField) {
        hiddenField.value = costs.shippingCost
      }
      
      // Update displayed cost
      const costText = formatCurrency(costs.shippingCost)
      
      // For collect accounts, show $0.00 with explanation that customer uses their own account
      // "Please Call to Arrange Shipping" is only for manual authorization (overweight items)
      if (costs.isCollectAccount) {
        shippingCostEl.innerHTML = `${hiddenField ? hiddenField.outerHTML : ''}${costText} <small class="text-muted">(${this.collectLabelValue})</small>`
      } else {
        shippingCostEl.innerHTML = `${hiddenField ? hiddenField.outerHTML : ''}${costText}`
      }
    }
    
    if (totalCostEl) {
      const hiddenField = totalCostEl.querySelector('input[type="hidden"]')
      if (hiddenField) {
        hiddenField.value = costs.total
      }
      totalCostEl.innerHTML = `${hiddenField ? hiddenField.outerHTML : ''}${formatCurrency(costs.total)}`
    }
  }

  /**
   * Shows/hides sections based on shipping terms selection
   * @param {Object} costs - Calculated costs object
   */
  updateShippingTermsVisibility(costs) {
    if (costs.isCollectAccount) {
      this.showCollectAccountFields()
    } else {
      this.showRateFields()
    }
  }

  /**
   * Shows collect account fields and hides rate selection
   */
  showCollectAccountFields() {
    // Hide only the price displays (not the service names)
    document.querySelectorAll('.option-price.rates').forEach(el => el.classList.add('hide'))
    
    // Show collect account field
    const collectField = document.querySelector('.collect-account-field')
    if (collectField) {
      collectField.classList.remove('d-none')
      collectField.classList.remove('hide')
    }
    
    // Make collect account required
    const collectInput = document.getElementById('collect_account')
    if (collectInput) {
      collectInput.required = true
    }
  }

  /**
   * Shows rate selection and hides collect account fields
   */
  showRateFields() {
    // Show the price displays
    document.querySelectorAll('.option-price.rates').forEach(el => el.classList.remove('hide'))
    
    // Hide collect account field
    const collectField = document.querySelector('.collect-account-field')
    if (collectField) {
      collectField.classList.add('d-none')
    }
    
    // Remove collect account requirement
    const collectInput = document.getElementById('collect_account')
    if (collectInput) {
      collectInput.required = false
    }
  }

  /**
   * Shows/hides freight forwarding section based on selection
   * @param {Object} costs - Calculated costs object
   */
  updateFreightForwarderVisibility(costs) {
    const freightSection = document.getElementById('freight-forwarding')
    if (!freightSection) return
    
    if (costs.hasFreightForwarder) {
      freightSection.classList.remove('d-none')
      freightSection.classList.remove('hide')
    } else {
      freightSection.classList.add('hide')
    }
  }
}
